import { memo } from 'react';
import type { StaffConfidence } from '../../types/staff';
import { cn } from '../../utils/cn';

interface ConfidenceRingProps {
  confidence: StaffConfidence | string;
  size?: number;
  className?: string;
}

const LEVEL: Record<string, { value: number; stroke: string; text: string }> = {
  high: { value: 1, stroke: 'stroke-green-400', text: 'text-green-300' },
  medium: { value: 0.66, stroke: 'stroke-blue-400', text: 'text-blue-300' },
  low: { value: 0.33, stroke: 'stroke-yellow-400', text: 'text-yellow-300' },
  none: { value: 0, stroke: 'stroke-glass-400', text: 'text-glass-400' },
};

/** Radial gauge for the steward's confidence in a profile, with the level as text inside. */
export const ConfidenceRing = memo(({ confidence, size = 96, className }: ConfidenceRingProps) => {
  const level = LEVEL[confidence] ?? LEVEL.none;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div
      className={cn('relative flex-shrink-0', className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Profile confidence: ${confidence || 'none'}`}
      title={`Profile confidence: ${confidence || 'none'}`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-glass-200/15" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - level.value)}
          className={cn('transition-[stroke-dashoffset] duration-700 ease-out', level.stroke)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={cn('text-sm font-bold capitalize', level.text)}>{confidence || 'none'}</span>
        <span className="text-[8px] uppercase tracking-[0.08em] text-glass-500 mt-1">confidence</span>
      </div>
    </div>
  );
});

ConfidenceRing.displayName = 'ConfidenceRing';
